import React from 'react';
import { Button, Dialog, DialogSurface, DialogTitle, DialogBody, DialogContent, DialogActions } from '@fluentui/react-components';
import { useLocalization } from '../Helpers/useLocalization';

// Convert common video-hosting URLs into an embeddable iframe src. Returns null
// for URLs that should be played directly via a <video> element (e.g. .mp4).
export const toEmbedUrl = (url: string): string | null => {
    try {
        const u = new URL(url);
        const host = u.hostname.replace(/^www\./, '');
        if (host === 'youtube.com' || host === 'm.youtube.com') {
            const id = u.searchParams.get('v');
            if (id) return `https://www.youtube.com/embed/${id}`;
            const embedMatch = u.pathname.match(/\/(embed|shorts)\/([^/?]+)/);
            if (embedMatch) return `https://www.youtube.com/embed/${embedMatch[2]}`;
        }
        if (host === 'youtu.be') {
            const id = u.pathname.slice(1).split('/')[0];
            if (id) return `https://www.youtube.com/embed/${id}`;
        }
        if (host === 'vimeo.com') {
            const id = u.pathname.split('/').filter(Boolean)[0];
            if (id && /^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}`;
        }
        if (host === 'player.vimeo.com') return url;
    } catch {
        return null;
    }
    return null;
};

interface IProductVideoModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    videoUrl: string;
    title?: string;
}

// Shared 16:9 video player dialog used by product cards and the product detail page.
const ProductVideoModal: React.FC<IProductVideoModalProps> = (props) => {
    const L = useLocalization();
    const embedUrl = toEmbedUrl(props.videoUrl);
    return (
        <Dialog open={props.open} onOpenChange={(_, d) => props.onOpenChange(d.open)}>
            <DialogSurface onClick={e => e.stopPropagation()} style={{ maxWidth: '900px', width: '90vw' }}>
                <DialogBody>
                    <DialogTitle>{props.title ?? 'Product Video'}</DialogTitle>
                    <DialogContent>
                        <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', height: 0 }}>
                            {embedUrl ? (
                                <iframe
                                    src={props.open ? `${embedUrl}${embedUrl.includes('?') ? '&' : '?'}autoplay=1` : undefined}
                                    title={props.title ?? 'Product video'}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0, borderRadius: 6 }}
                                />
                            ) : (
                                <video
                                    src={props.videoUrl}
                                    controls
                                    autoPlay
                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: 6, background: '#000' }}
                                />
                            )}
                        </div>
                    </DialogContent>
                    <DialogActions>
                        <Button appearance="secondary" onClick={() => props.onOpenChange(false)}>{L('localization_common_close')}</Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default ProductVideoModal;
